import React from "react";
import { titleCase } from "./api";

export const Btn = ({ variant = "primary", className = "", children, ...rest }) => {
    const styles = {
        primary: "bg-gold text-black hover:brightness-110",
        ghost: "border border-white/15 hover:border-gold hover:text-gold",
        danger: "border border-red-500/40 text-red-400 hover:bg-red-500/10",
        subtle: "bg-white/5 hover:bg-white/10",
    }[variant];
    return (
        <button
            className={`mono text-[11px] tracking-[0.14em] px-4 py-2.5 rounded-none font-bold transition-all disabled:opacity-40 disabled:cursor-not-allowed ${styles} ${className}`}
            {...rest}
        >
            {children}
        </button>
    );
};

export const Card = ({ title, action, children, className = "", ...rest }) => (
    <section className={`bg-bg1 border border-white/10 rounded-sm ${className}`} {...rest}>
        {(title || action) && (
            <header className="flex items-center justify-between gap-4 px-5 py-4 border-b border-white/10">
                <h3 className="font-display text-lg tracking-tight">{title}</h3>
                {action}
            </header>
        )}
        <div className="p-5">{children}</div>
    </section>
);

export const StatCard = ({ label, value, sub, tone = "default", onClick, testId }) => {
    const tones = {
        default: "text-white",
        gold: "text-gold",
        danger: "text-red-400",
        good: "text-lime",
    };
    return (
        <div
            onClick={onClick}
            data-testid={testId}
            className={`bg-bg1 border border-white/10 rounded-sm p-5 ${onClick ? "cursor-pointer hover:border-gold/50 transition-colors" : ""}`}
        >
            <div className="mono text-[10px] tracking-[0.2em] text-zinc-500">{label}</div>
            <div className={`font-display text-3xl mt-2 ${tones[tone]}`}>{value}</div>
            {sub && <div className="text-xs text-zinc-500 mt-1">{sub}</div>}
        </div>
    );
};

export const Field = ({ label, hint, required, children, className = "" }) => (
    <label className={`block ${className}`}>
        <span className="mono text-[10px] tracking-[0.18em] text-zinc-400">
            {label}{required && <span className="text-gold"> *</span>}
        </span>
        <div className="mt-1.5">{children}</div>
        {hint && <span className="block mt-1 text-[11px] text-zinc-500">{hint}</span>}
    </label>
);

const inputCls =
    "w-full bg-bg0 border border-white/12 text-white px-3 py-2.5 text-sm rounded-none outline-none focus:border-gold transition-colors placeholder:text-zinc-600";

export const Input = ({ className = "", ...rest }) => <input className={`${inputCls} ${className}`} {...rest} />;
export const Textarea = ({ className = "", ...rest }) => <textarea className={`${inputCls} ${className}`} {...rest} />;
export const Select = ({ options = [], className = "", children, ...rest }) => (
    <select className={`${inputCls} ${className}`} {...rest}>
        {children ||
            options.map((o) =>
                typeof o === "string"
                    ? <option key={o} value={o}>{titleCase(o)}</option>
                    : <option key={o.value} value={o.value}>{o.label}</option>
            )}
    </select>
);

export const Badge = ({ tone = "muted", children }) => {
    const tones = {
        muted: "bg-white/8 text-zinc-300",
        gold: "bg-gold/15 text-gold border border-gold/30",
        good: "bg-lime/15 text-lime border border-lime/30",
        danger: "bg-red-500/15 text-red-400 border border-red-500/30",
        warn: "bg-orange-500/15 text-orange-300 border border-orange-500/30",
    };
    return (
        <span className={`inline-flex items-center mono text-[9px] tracking-[0.14em] px-2 py-1 ${tones[tone]}`}>
            {children}
        </span>
    );
};

export const statusTone = (status = "") =>
    ({
        active: "good", received: "good", delivered: "good", ok: "good",
        coming_soon: "gold", pending_approval: "gold", ordered: "gold", processing: "gold", partially_received: "gold",
        draft: "muted", cancelled: "danger", discontinued: "danger", out_of_stock: "danger", expired: "danger", low: "warn",
        temporarily_unavailable: "warn",
    }[status] || "muted");

export const Table = ({ head = [], children, empty, testId }) => (
    <div className="overflow-x-auto -mx-5 px-5" data-testid={testId}>
        <table className="w-full min-w-[720px] text-sm">
            <thead>
                <tr className="text-left border-b border-white/10">
                    {head.map((h) => (
                        <th key={h} className="mono text-[10px] tracking-[0.16em] text-zinc-500 font-normal py-3 pr-4 whitespace-nowrap">
                            {h}
                        </th>
                    ))}
                </tr>
            </thead>
            <tbody className="divide-y divide-white/5">{children}</tbody>
        </table>
        {empty}
    </div>
);

export const Td = ({ className = "", children, ...rest }) => (
    <td className={`py-3.5 pr-4 align-middle ${className}`} {...rest}>{children}</td>
);

export const Empty = ({ msg }) => <div className="py-12 text-center text-zinc-500 text-sm">{msg}</div>;

export const Modal = ({ open, onClose, title, children, wide = false, testId }) => {
    React.useEffect(() => {
        if (!open) return;
        const onKey = (e) => { if (e.key === "Escape") onClose(); };
        window.addEventListener("keydown", onKey);
        return () => window.removeEventListener("keydown", onKey);
    }, [open, onClose]);
    if (!open) return null;
    return (
        <div className="fixed inset-0 z-[60] flex items-start justify-center overflow-y-auto p-4 md:p-10" data-testid={testId}>
            <div className="fixed inset-0 bg-black/75 backdrop-blur-sm" onClick={onClose} />
            <div className={`relative w-full ${wide ? "max-w-4xl" : "max-w-xl"} bg-bg1 border border-white/12 rounded-sm shadow-2xl`}>
                <header className="flex items-center justify-between px-5 py-4 border-b border-white/10">
                    <h3 className="font-display text-xl tracking-tight">{title}</h3>
                    <button onClick={onClose} data-testid="modal-close" className="mono text-xs text-zinc-400 hover:text-gold">CLOSE</button>
                </header>
                <div className="p-5 max-h-[75vh] overflow-y-auto">{children}</div>
            </div>
        </div>
    );
};

export const PageHead = ({ title, sub, action }) => (
    <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4 mb-6">
        <div>
            <h1 className="font-display text-3xl md:text-4xl tracking-tight">{title}</h1>
            {sub && <p className="text-sm text-zinc-500 mt-1.5">{sub}</p>}
        </div>
        {action}
    </div>
);

export const Toggle = ({ label, value, onChange, testId, hint }) => (
    <Field label={label} hint={hint}>
        <Select value={value ? "yes" : "no"} data-testid={testId}
            options={[{ value: "yes", label: "Yes" }, { value: "no", label: "No" }]}
            onChange={(e) => onChange(e.target.value === "yes")} />
    </Field>
);
